import styles from "./homedashboard.module.css";

import React, { useEffect, useImperativeHandle, useState, Ref } from "react";
import { DialogProperties } from 'components/layout/dialogs/DialogProperties';
import MetaIdbRepository from "repositories/idb/MetaIdbRepository";
import PilotApiRepository from "repositories/api/PilotApiRepository";
import { DialogConfirm } from "components/layout/dialogs/components/DialogConfirm";
import { getLoggedInPersonId, onGuestCheckAsync } from "components/helpers/AuthHelpers";
import Spacer from "components/layout/Spacer";
import { IconPicker } from "components/layout/icons/IconPicker";
import { useSuccessNotification, useWarningNotification } from 'components/hooks/useAppStore';

export interface HomeGuestDepartmentHandle {
    onRefresh: () => void;
}

interface HomeGuestDepartmentProps {
    ref: Ref<HomeGuestDepartmentHandle>,
}

export const HomeGuestDepartment = ({ ref }: HomeGuestDepartmentProps) => {

    useImperativeHandle(ref, () => ({
        onRefresh() {
            initialize();
        }
    }));

    const [guestPilotStation, setGuestPilotStation] = useState<null | { displayName: any, sourceDisplayName : any}>(null);
    const [showDialog, setShowDialog] = useState(false);

    const idbPrimaryKey = `GuestPilotStation-${getLoggedInPersonId()}`

    useEffect(() => {

        initialize();

        window.addEventListener('online', initializeOnline);
        window.addEventListener('offline', initializeOffline);

        return () => {
            window.removeEventListener('online', initializeOnline);
            window.removeEventListener('offline', initializeOffline);
        }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        (guestPilotStation) &&
        <>
            <div
                className={styles.bar}
                style={{ backgroundColor: "var(--home-warning-bg)" }}
                onClick={
                    () => {
                        if (!navigator.onLine) return;
                        setShowDialog(true)
                    }}
            >
                <div className={styles.barContent}>
                    <div>
                        <div className="k-font-bold">Gjestelos</div>
                        <Spacer height={5} />
                        <div>
                            {
                                navigator.onLine ?
                                    <div>Du er gjestelos p&aring; <b>{guestPilotStation.displayName}</b>. Klikk her for &aring; avslutte.</div>
                                    :
                                    <div>Du er gjestelos p&aring; <b>{guestPilotStation.displayName}</b>.For &aring; avslutte m&aring; du v&aelig;re p&aring; nett.</div>
                            }
                        </div>
                    </div>
                    <div>
                        <IconPicker icon="Identification-badge" size={24}/>
                    </div>
                </div>
            </div>
            <Spacer height={20} />
            {
                showDialog &&
                <HomeGuestDepartmentDialog
                    idbPrimaryKey={idbPrimaryKey}
                    guestPilotStation={guestPilotStation}
                    initialize={() => { initializeOnline() }}
                    setShowDialog={(e) => setShowDialog(e)}
                />
            }
        </>
    )

    function initialize() {
        if (navigator.onLine) {
            initializeOnline();
        } else {
            initializeOffline();
        }
    }

    async function initializeOnline() {
        const data = await onGuestCheckAsync();
        setGuestPilotStation(data);
    }

    async function initializeOffline() {
        let localGuestPilotStation: any = null;
        localGuestPilotStation = await MetaIdbRepository.getGuestPilotStationAsync(idbPrimaryKey);

        if (localGuestPilotStation) {
            setGuestPilotStation(localGuestPilotStation.value);
        } else {
            setGuestPilotStation(null);
        }
    }
    
}

interface HomeGuestDepartmentDialogProps {
    idbPrimaryKey: string,
    guestPilotStation: any,
    initialize: () => void,
    setShowDialog: (e:boolean) => void
}

const HomeGuestDepartmentDialog = (props: HomeGuestDepartmentDialogProps) => {

    const addSuccessNotification = useSuccessNotification();
    const addWarningNotification = useWarningNotification();


    return (
        <DialogConfirm properties={{
            ...DialogProperties,
            title: "Avslutte som gjestelos",
            onClose: () => props.setShowDialog(false),
            onClick: onConfirmRemove
        }} >
            &Oslash;nsker du &aring; avslutte som gjestelos p&aring; {props.guestPilotStation.displayName}?<br />
            N&aring;r du bekrefter, vil losformidler igjen se deg under {props.guestPilotStation.sourceDisplayName}.
        </DialogConfirm>
    )

    async function onConfirmRemove() {
        props.setShowDialog(false);

        const loggedInPersonId = getLoggedInPersonId();
        const response = await PilotApiRepository.removeGuestPilotStationAsync(loggedInPersonId);
        if (response.ok) {
            await MetaIdbRepository.deleteGuestPilotStationAsync(props.idbPrimaryKey);
            addSuccessNotification(`Du ble fjernet som gjestelos fra ${props.guestPilotStation.displayName}`);
        } else {
            addWarningNotification(`Kunne ikke fjerne deg som gjestelos fra ${props.guestPilotStation.displayName}`);
        }

        props.initialize();
    }
}
